import { useMemo, useState } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useRealtimeValidation } from '../../../../container/hooks/useRealtimeValidation';
import { useMembershipModalState } from '../../../../modalContext';

type Result = ModelHookResult<
	{
		amount: number;
		value: number;
		currency: string;
		isSelected: boolean;
		currencySymbol: string;
		currencySymbolPosition: 'before' | 'after';
		decimalSeparator: string;
		errorMessage: string | null;
		showFractionNumber: boolean;
		isCustomAmount: boolean;
		placeholder: string;
		isSunriseEnabled: boolean;
	},
	{
		onClick: () => void;
		onChange: (value: string) => void;
		onFocus: () => void;
	}
>;

export function useAmountSelector(): Result {
	const [{ amount, currencyCode, validationErrors, isSunriseEnabled }, { setAmount }] = useMembershipModalState();
	const { getCurrencySymbol, getCurrencySymbolPosition, getDecimalSeparator, isZeroDecimalCurrency, translate } =
		useI18n();

	const [isCustomAmount, setIsCustomAmount] = useState<boolean>(false);
	const [customAmount, setCustomAmount] = useState<number>(0);

	const track = useTracking();

	useRealtimeValidation();

	const originalAmount = useMemo(() => {
		return amount;
		// This is intentional, we only want to run this once to store initial amount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		data: {
			amount: originalAmount,
			currency: currencyCode,
			isSelected: !isCustomAmount,
			isCustomAmount,
			errorMessage: customAmount !== 0 ? (validationErrors?.amount ?? null) : null,
			value: isCustomAmount ? amount : 0,
			currencySymbol: getCurrencySymbol(currencyCode),
			currencySymbolPosition: getCurrencySymbolPosition(currencyCode),
			decimalSeparator: getDecimalSeparator(),
			showFractionNumber: !isZeroDecimalCurrency(currencyCode),
			placeholder: translate('corgi.co.promote.custom_amount'),
			isSunriseEnabled,
		},
		actions: {
			onClick: () => {
				setAmount(originalAmount);
				setCustomAmount(0);
				setIsCustomAmount(false);
				void track('post_promote_default_amount_selected');
			},
			onFocus: () => {
				setAmount(customAmount);
				setIsCustomAmount(true);
				void track('post_promote_other_amount_selected');
			},
			onChange: (value) => {
				// Only update `amount` if the new input value is numeric. Otherwise parsing the string would yield NaN, which would
				// clear `amount` and be a bad/surprising UX
				if (/^\d*$/.test(value)) {
					const newAmount = Number(value);
					setAmount(newAmount);
					setCustomAmount(newAmount);
				}
			},
		},
	};
}
